import { Button, Grid, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import demoIrrigationRecords from "config/demo/irrigations.json";
import IrrigationForm from "components/agro/irrigations/IrrigationForm";

const Irrigations = () => {
  const [records] = useState((demoIrrigationRecords && demoIrrigationRecords.slice) ? demoIrrigationRecords.slice(0, 10) : []);
  const [recordDetails, setRecordDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  const [modalForm, setModalForm] = useState({
    visible: false,
    itemId: null,
    action: null,
  });

  const columnsMapped = useMemo(
    () =>
      records.map((item) => ({
        ...item,
        actions: (
          <>
            <ActionIcon
              name={recordDetails?.id === item.id ? strings.records.hide : strings.records.show}
              icon={recordDetails?.id === item.id ? "VisibilityOff" : "Visibility"}
              color="primary"
              onClick={() => {
                recordDetails?.id === item.id ? setRecordDetails(null) : setRecordDetails(item);
              }}
            />
            <ActionIcon
              name={strings.records.edit}
              icon="Edit"
              color="primary"
              onClick={() => openModal("update", item.id)}
            />
            <ActionIcon
              name={strings.records.delete}
              icon="Delete"
              color="red.60"
              onClick={() =>
                setModalDetails({
                  visible: true,
                  itemId: item.id,
                  itemName: item.recipeName || item.id,
                })
              }
            />
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, recordDetails?.id]
  );

  const openModal = (action, id = null) => setModalForm({ visible: true, action, itemId: id });

  const tableColumns = [
    { key: "date", label: strings.table.headers.date, _style: { width: "18%" } },
    { key: "farmId", label: strings.table.headers.farm, _style: { width: "20%" } },
    { key: "applicationType", label: strings.table.headers.applicationType, _style: { width: "18%" } },
    { key: "recipeId", label: strings.table.headers.recipe, _style: { width: "18%" } },
    { key: "actions", label: "", _style: { width: "15%" } },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonAdd}
        children={strings.records.addRecord}
        variation="primary"
        onClick={() => openModal("create")}
      />
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <SearchField label={strings.table.search.label} placeholder={strings.table.search.placeholder} />
        </Grid>

        <CTable
          className="table-centered"
          items={columnsMapped}
          columns={tableColumns}
          tableHeadProps={{ color: "light" }}
          responsive
          hover
        />

        <CCollapse visible={!!recordDetails?.id} style={styles.collapseCard}>
          <CCard key={recordDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{recordDetails?.recipeName || recordDetails?.id}"
              </h4>

              <p className="text-muted"><b>{strings.details.date}:</b> {recordDetails?.date ? moment(recordDetails.date).format("YYYY-MM-DD") : "-"}</p>
              <p className="text-muted"><b>{strings.details.farm}:</b> {recordDetails?.farmName ?? recordDetails?.farmId ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.lot}:</b> {recordDetails?.lotName ?? recordDetails?.lotId ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.applicationType}:</b> {recordDetails?.applicationType ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.performedBy}:</b> {recordDetails?.performedByName ?? recordDetails?.performedById ?? "-"}</p>

              <hr />

              <p className="text-muted"><b>{strings.details.recipeId}:</b> {recordDetails?.recipeId ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.prescribingAgronomistId}:</b> {recordDetails?.prescribingAgronomistId ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.applicationUnits}:</b> {recordDetails?.applicationUnits ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.recipeDetailsTable}:</b> {recordDetails?.recipeDetailsTable ? JSON.stringify(recordDetails.recipeDetailsTable) : "-"}</p>

              <p className="text-muted"><b>{strings.details.applicationUnit}:</b> {recordDetails?.applicationUnit ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.photoEvidence}:</b> {(Array.isArray(recordDetails?.photoEvidence) ? recordDetails.photoEvidence.join(", ") : recordDetails?.photoEvidence) ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.createdAt}:</b> {recordDetails?.createdAt ? moment(recordDetails.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}</p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, { name: modalDetails.itemName })}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
      />

      {modalForm.action && (
        <CModal size="xl" alignment="center" visible={modalForm.visible} onClose={() => setModalForm({ visible: false })} backdrop="static">
          <CModalHeader>
            <CModalTitle>{modalForm.action === "create" ? strings.modalForm.addTitle : strings.modalForm.updateTitle}</CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <IrrigationForm
                type={modalForm.action}
                initialValues={modalForm.action === "update" ? records.find((r) => r.id === modalForm.itemId) || {} : {}}
                onSubmit={() => {}}
                onCancel={() => setModalForm({ visible: false })}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Riegos / Fertilizaciones",
    records: {
      addRecord: "Registrar Riego/Fertilización",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    table: {
      headers: {
        date: "Fecha",
        farm: "Finca",
        lot: "Lote",
        applicationType: "Tipo",
        recipe: "Receta",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles Registro Riego/Fertilización",
      date: "Fecha de aplicación",
      farm: "Finca",
      lot: "Lote",
      applicationType: "Tipo",
      performedBy: "Persona que realiza",
      recipeId: "Receta",
      prescribingAgronomistId: "Agrónomo que receta",
      applicationUnits: "Unidades de aplicación",
      recipeDetailsTable: "Detalle receta",
      applicationUnit: "Unidad de aplicación",
      photoEvidence: "Evidencia fotográfica",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el registro {name}?",
    },
    modalForm: {
      addTitle: "Registrar Riego/Fertilización",
      updateTitle: "Actualizar Registro",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  collapseCard: { marginTop: "15px" },
};

export default Irrigations;
